// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  ChartEvent,
  BarController,
  BarElement,
  Point,
  PointElement,
  Legend,
  LegendItem,
  LineController,
  LineElement,
  LinearScale,
  TimeScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  TimeUnit,
  ActiveElement,
  ChartDataset,
} from "chart.js";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";
import ResizeObserver from "resize-observer-polyfill";

import { isSet, roundToMinDigits } from "utils";
import {
  doKiloConversion,
  getEnergyUnit,
  getPowerUnit,
  getGraphMinUnits,
  getPanelSelection,
} from "guiutils";
import {
  getColor,
  hexToRgba,
  COLOR_SELECT,
  getTimestampsForTrendData,
  roundTimestamp,
} from "guiutils";
import { ActionType, IAction, raiseAction } from "actions";
import { IActionTypeZoom, IActionTypeSelectDashboardItem } from "actions";
import { TimePeriod, DataState, DashboardPanel } from "types";
import { IState } from "states/states";
import { GraphViewMode, IPanelSelection } from "states/cachedstates";
import { IEnergyMeter, ITariffCircuit } from "parameter";
import { ITrendData, ITrendDataRow } from "states/trendingstates";
import TariffCircuitListModel from "model/tariffcircuitlist";

import { IDropdownSelectOption } from "./DropdownSelect";
import { ReactComponent as IconZoomFit } from "static/iconZoomFit.svg";
import cssContent from "content.scss";

// register used chartjs elements
ChartJS.register(
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  Legend,
  Tooltip,
  zoomPlugin,
);

const DATASET_REFERENCE_IDX_OFFSET = 0;
const DATASET_OVERSHOT_IDX_OFFSET = 1;

// if we have more than this items in the legend, we hide zero values in the tooltip
const NR_TLS_HIDE_ZERO = 10;

interface IEnergyGraphExtProps {
  showLegend: boolean;
  dashboardIndex?: number;
}

interface IEnergyGraphProps {
  dataState: DataState;
  trendLoadingProgress: number;
  selectedDashboardIndex: number;
  trendMember: "energy" | "power" | "costs";
  startDate: number | undefined;
  endDate: number | undefined;
  trendData: ITrendData;
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selection: IPanelSelection | undefined;
  graphViewMode: GraphViewMode;
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
  // deviceTimestamp: number | undefined;
  timezoneOffset: number | undefined;
}

const mapStateToProps = (state: IState, ownProps: IEnergyGraphExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    DashboardPanel.ENERGY,
    ownProps.dashboardIndex,
  );
  const props: IEnergyGraphProps = {
    dataState: state.scheduler.trendLoadingState,
    trendLoadingProgress: state.scheduler.trendLoadingProgress,
    selectedDashboardIndex: index,
    selection,
    trendMember: state.gui.trendMember,
    startDate: state.gui.trendPeriod.startDate,
    endDate: state.gui.trendPeriod.endDate,
    trendData: state.trending.trendData,
    tariffCircuits: state.parameter?.tariffline ?? [],
    energyMeters: state.parameter?.energymeter ?? [],
    graphViewMode: selection?.graphViewMode ?? GraphViewMode.BAR,
    xZoom: state.gui.trendPeriod.timePeriod,
    minX: state.gui.trendPeriod.zoomMin,
    maxX: state.gui.trendPeriod.zoomMax,
    // we only need the device timestamp if graph start date is not set
    // @@@Marc: do not use the deviceTimestamp for now, since this timestamp is changeing
    // with the live-data every 5 Seconds, which would cause a refresh of the Graphs
    // deviceTimestamp:
    //   state.gui.trendPeriod.startDate === undefined ? state.data.timestamp : undefined,
    timezoneOffset: state.site.tzOffset,
  };
  return props;
};

interface IEnergyGraphState {
  redrawToggle: boolean;
}

interface IEnergyGraphDispatch {
  onDropdownTariffSelectionChanged: (index: number, selectedIds: number[]) => void;
  onZooming: (minX?: number, maxX?: number) => void;
  onDetailClick: (timestamp: number, timePeriod: TimePeriod) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnergyGraphDispatch = {
    onDropdownTariffSelectionChanged: (index: number, selectedIds: number[]) => {
      const payload: IActionTypeSelectDashboardItem = { index, selectedIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_TARIFFCIRCUITS, payload));
    },
    onZooming: (minX?: number, maxX?: number) => {
      const actionPayload: IActionTypeZoom = {
        identifier: "trendPeriod",
        minX,
        maxX,
      };
      dispatch(raiseAction(ActionType.ACT_ZOOM_GRAPH, actionPayload));
    },
    onDetailClick: (timestamp: number, timePeriod: TimePeriod) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_TIMESTAMP, timestamp));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  resetZoom: {
    id: "reset zoom",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
});

// State is never set so we use the 'undefined' type.
class EnergyGraphComponent extends React.Component<
  IEnergyGraphProps & IEnergyGraphExtProps & IEnergyGraphDispatch & WrappedComponentProps,
  IEnergyGraphState
> {
  state = {
    redrawToggle: false,
  };

  private resizeObserver: ResizeObserver | undefined;
  private chartRef = React.createRef<Chart>();
  private readonly compRef = React.createRef<HTMLDivElement>();
  private startTime?: number;
  private endTime?: number;
  private data: number[][][] = []; // tariffline array of [timestamp, y] arrays
  private doKiloConvertion = false;
  private refData: number[] = []; // array of ref values, index equals data index
  private oldHeight = 0;
  private height = 0;
  private minUnits: TimeUnit = "month";
  private maxValue = 0;
  private zoomLock = false;
  private panLock = false;
  // the internal data-structure looks like:
  // --> allTariffs hold all available tariffs for that view
  //      each tariff (IDropdownSelectOption) contains the information
  //        "visible" which indicates if the current tariff should get shown
  // --> this.props.selectedTariffs holds the tariffs selected by the user
  //      via dropDown or via legend (which both shows the current state)
  // --> charts.data.datasets
  //      all tariffs are added to the datasets
  //        the ones were the visible is set to false, no data-points are added
  //        this is needed to show the legend all the time to turn on/off the curves
  //      special indizes:
  //        at the end of the data-sets (this.allTariffs.lenght and this.allTariffs.lenght + 1)
  //        (see constants DATASET_REFERENCE_IDX_OFFSET and DATASET_OVERSHOT_IDX_OFFSET)
  //        the reference and overshoot datasets are added, if only one curve is selected and
  //        reference data is available for the selected curve!!
  private allTariffCircuits: IDropdownSelectOption[] = [];

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((elem: ResizeObserverEntry[]) => {
      if (this.chartRef.current && elem[0].contentRect.height) {
        // yes, this is odd
        // on screen build-up on the dashboard this graphs parent div gets resized twice
        // however, the graph just resizes the first one. We need to force screen update
        // by unmount/remount which we achieve by changing the divs key attribute
        // since the first resize is OK, we use oldHeight and a 2-stage FIFO
        this.oldHeight = this.height;
        this.height = elem[0].contentRect.height;
        //console.log("forcing update", this.oldHeight, this.height);
      }
    });
    if (this.compRef.current) this.resizeObserver.observe(this.compRef.current);
    else console.warn("did not register graph size observer");
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }

  public render() {
    const showAllChildren = this.props.selection?.hierarchyId === undefined;
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    const tariffCircuitIndexes = tcModel.getTariffCircuits(
      this.props.selection?.energySource,
      false,
      this.props.selection?.hierarchyId,
      this.props.selection?.hierarchyId === undefined,
      this.props.selection?.category,
      showAllChildren,
    );

    const availableTariffCircuits = tcModel.makeOptionsArray(
      tariffCircuitIndexes,
      this.props.selection?.tariffCircuitIds,
      showAllChildren,
      true,
    );

    this.allTariffCircuits = [...availableTariffCircuits];

    // make some general calculations
    this.preProcessData();

    // check for existing data
    if (
      this.props.dataState !== DataState.SAVE_REQ &&
      this.data.every((curItem) => curItem.length === 0)
    ) {
      return (
        <div id="energygraph" className="graph nodata" ref={this.compRef}>
          {this.props.dataState === DataState.SAVE_FAIL ? (
            <FormattedMessage id="Failed to load data." />
          ) : this.props.minX || this.props.maxX ? (
            <>
              {this.allTariffCircuits.length > 0 ? (
                <FormattedMessage id="No data available. Try reset zooming." />
              ) : (
                <FormattedMessage id="Please select tariff circuit." />
              )}
              <div
                className="graph-zoom"
                onClick={this.resetZoom.bind(this)}
                title={this.props.intl.formatMessage(messages.resetZoom)}
              >
                <IconZoomFit />
              </div>
            </>
          ) : this.allTariffCircuits.length > 0 ? (
            <FormattedMessage id="No data available." />
          ) : (
            <FormattedMessage id="Please select tariff circuit." />
          )}
        </div>
      );
    }

    return (
      <div id="energygraph" className="graph" ref={this.compRef} key={this.oldHeight}>
        {isSet(this.props.minX) || isSet(this.props.maxX) ? (
          <div
            className="graph-zoom"
            onClick={this.resetZoom.bind(this)}
            title={this.props.intl.formatMessage(messages.resetZoom)}
          >
            <IconZoomFit />
          </div>
        ) : (
          <></>
        )}
        {this.props.dataState === DataState.SAVE_REQ && (
          <div className="loading-spinner-box">
            <div id="loading-spinner"></div>
            <div id="loading-text">
              {this.props.trendLoadingProgress > 0 ? (
                <>{Math.round(100 * this.props.trendLoadingProgress)} %</>
              ) : (
                <></>
              )}
            </div>
          </div>
        )}
        <Chart
          ref={(c) => {
            if (isSet(c)) this.chartRef = c as any;
          }}
          type={this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar"}
          redraw={true}
          data={this.createGraphData()}
          options={this.createGraphOptions()}
          plugins={[
            {
              id: "customHover",
              afterEvent: (chart: ChartJS, args: any, opts: any) => {
                const evt: ChartEvent = args.event;
                const mouseX = evt.x ?? 0;
                const mouseY = evt.y ?? 0;
                const inArea =
                  mouseX > chart.chartArea.left &&
                  mouseX < chart.chartArea.right &&
                  mouseY > chart.chartArea.top &&
                  mouseY > chart.chartArea.bottom;
                if (
                  !inArea ||
                  (evt.type !== "click" && evt.type !== "mousemove") ||
                  this.props.dashboardIndex !== undefined
                ) {
                  return;
                }

                const xValue = chart.scales.x.getValueForPixel(evt.x ?? 0) ?? 0;
                const yValue = chart.scales.y.getValueForPixel(evt.y ?? 0) ?? 0;
                const xValid =
                  xValue >= chart.scales.x.min &&
                  xValue <= chart.scales.x.max &&
                  xValue < Date.now();
                const yValid = yValue < chart.scales.y.min;

                const element = document
                  .getElementById("energygraph")
                  ?.getElementsByTagName("canvas")[0];

                if (isSet(element)) {
                  element.style.cursor = xValid && yValid ? "pointer" : "default";
                }

                xValid &&
                  yValid &&
                  evt.type === "click" &&
                  this.props.onDetailClick(Math.round(xValue / 1000 / 900) * 900, this.props.xZoom);
              },
            },
          ]}
        />
      </div>
    );
  }

  private isOnlyOneSelected() {
    // true if
    // - we have only 1 tariff circuit available
    // - we have only 1 selection
    return (
      this.allTariffCircuits.length === 1 || this.props.selection?.tariffCircuitIds?.length === 1
    );
  }

  private preProcessData() {
    // some general data pre-processing
    const { startDate, endDate, allTimestamps, usedTimestamps } = getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.xZoom,
      this.isOnlyOneSelected(),
    );

    this.startTime = this.props.minX ? this.props.minX / 1000 : startDate;
    this.endTime = this.props.maxX ? this.props.maxX / 1000 : endDate;
    this.minUnits = getGraphMinUnits(this.endTime! - this.startTime!);

    // this.tlIndex = [];
    this.data = [];
    this.refData = [];
    // check if only 1 tariff is selected
    const hasReference = this.isOnlyOneSelected() && this.props.trendMember === "power";
    // get index from from selected single-line
    const dataIdx = this.allTariffCircuits.findIndex((item) => item.visible === true);

    // loop through sorted tariff circuits
    const oldMax = this.maxValue;
    this.maxValue = 0;
    const tsMaxValue: (number | undefined)[] = [];

    this.allTariffCircuits.forEach((curTariff, tariffIdx) => {
      if (curTariff.index >= 0) {
        const curCircuit = this.props.tariffCircuits[curTariff.index];
        // is this tariff line active and selected?
        if (curCircuit.webActive) {
          // not all timestamps may have a value for this TL associated, therefore we cannot use map()
          const dataset: number[][] = [];
          // const curTlId = this.props.tariffId >= 0 ? this.props.tariffId : curIndex;
          usedTimestamps.forEach((curTimestamp, tsIdx) => {
            if (curTariff.visible) {
              let value: number | undefined;
              let refValue: number | undefined;
              // find data row, either matching timestamp or matching rounded timestamp
              let dataRow: ITrendDataRow = this.props.trendData[curTimestamp]?.[curTariff.index];
              if (!dataRow) {
                // search for rounded match
                const matchingTimestamp = allTimestamps.find(
                  (item) =>
                    roundTimestamp(item, this.isOnlyOneSelected()) === curTimestamp &&
                    this.props.trendData[item]?.[curTariff.index],
                );

                if (matchingTimestamp) {
                  dataRow = this.props.trendData[matchingTimestamp][curTariff.index];
                }
              }
              if (dataRow && isSet(dataRow[this.props.trendMember])) {
                value = dataRow[this.props.trendMember]!;
              }

              // determine maximum: accumulate all values of each timestamp
              if (this.startTime! < curTimestamp && this.endTime! > curTimestamp) {
                if (this.props.graphViewMode === GraphViewMode.BAR) {
                  tsMaxValue[tsIdx] = (tsMaxValue[tsIdx] ?? 0) + (value ?? 0);
                } else {
                  if (isSet(value) && (!isSet(tsMaxValue[tsIdx]) || tsMaxValue[tsIdx]! < value)) {
                    tsMaxValue[tsIdx] = value;
                  }
                }
              }

              // for power values we also add reference data; power values will only consist of one series
              if (hasReference && isSet(dataRow?.targetpower)) {
                refValue = dataRow.targetpower;
                if (!isSet(tsMaxValue[tsIdx]) || refValue > tsMaxValue[tsIdx]!) {
                  tsMaxValue[tsIdx] = refValue;
                }
              }

              // we need to add even inexisting values (on bar-charts only!) if we show multiple data sets
              // otherwise the bars will have different widths which looks strange
              if (
                isSet(value) ||
                (this.props.graphViewMode !== GraphViewMode.LINE && !this.isOnlyOneSelected())
              ) {
                dataset.push([curTimestamp, value ?? NaN]);
                // only add reference-data if the current tariff
                // is the only-one selected
                if (hasReference && dataIdx === tariffIdx) {
                  this.refData.push(refValue ?? NaN);
                }
              }
            }
          });
          this.data.push(dataset);
        }
      }
    });

    // derive maximum
    this.maxValue = tsMaxValue.reduce(
      (prev, cur) => (!isSet(cur) || (isSet(prev) && prev > cur) ? prev : cur),
      0,
    )!;

    // check whether we need to multiply everything by 10000
    this.doKiloConvertion =
      doKiloConversion(this.maxValue) && ["energy", "power"].includes(this.props.trendMember);
    if (this.doKiloConvertion) {
      this.data.forEach((curSeries) => {
        curSeries.forEach((curData) => {
          curData[1] *= 1000;
        });
      });
      this.refData = this.refData.map((curItem) => 1000 * curItem);
      this.maxValue *= 1000;
    }
    // ensure to have at least 2 data sets in bar graphs to avoid the problem with bars being too
    // thick if only a single data item is shown
    if (usedTimestamps.length === 1 && this.props.graphViewMode !== GraphViewMode.LINE) {
      // append a zero data item
      const startDate = usedTimestamps[0];
      const daysToShift =
        this.props.xZoom === TimePeriod.DAY
          ? 1 / 24
          : this.props.xZoom === TimePeriod.WEEK
          ? 1
          : this.props.xZoom === TimePeriod.MONTH
          ? 7
          : /* this.props.xZoom === TimePeriod.YEAR */ 30;
      const newDate = startDate + daysToShift * 24 * 3600;
      // add it to each tariff line
      this.data.forEach((curItem) => curItem.push([newDate, 0]));
    }

    if (this.maxValue === 0) {
      this.maxValue = oldMax;
    }
    this.maxValue = this.maxValue * 1.05;
  }

  private getDataUnit(seriesIndex?: number) {
    // const tlId = this.tlIndex[seriesIndex ?? 0] ?? 0;
    const tlId = this.allTariffCircuits[seriesIndex ?? 0]?.index;
    let unit: string | undefined = "";
    // if we did not specify the tariff circuit, we take the very first one
    if (this.props.trendMember === "energy") {
      unit = getEnergyUnit(this.props.tariffCircuits[tlId]?.unit, this.doKiloConvertion);
    } else if (this.props.trendMember === "power") {
      unit = getPowerUnit(this.props.tariffCircuits[tlId]?.unit, this.doKiloConvertion);
    } else if (this.props.trendMember === "costs") {
      unit = "EUR";
    }
    return unit ?? "";
  }

  private createGraphData() {
    const data: ChartData<"bar" | "line", ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    // do not provide data while still loading
    if (this.props.dataState === DataState.SAVE_REQ) {
      return data;
    }
    const hasRefData =
      this.isOnlyOneSelected() &&
      this.refData.length > 0 &&
      this.refData.filter((val) => !isNaN(val)).length > 0;

    // prepare all graph-data (for legend at least!)
    this.data.forEach((curData, curSeriesIndex) => {
      // const tlId = this.tlIndex[curSeriesIndex];
      const points: ScatterDataPoint[] = [];

      if (this.allTariffCircuits[curSeriesIndex].visible === true) {
        curData.forEach((curItem) => {
          points.push({
            x: curItem[0] * 1000,
            y: curItem[1],
          });
        });
      }
      const isSelectedHierarchy =
        this.allTariffCircuits[curSeriesIndex].id === this.props.selection?.hierarchyId;

      const color = getColor(this.allTariffCircuits[curSeriesIndex].index);

      const showLinePoints = this.props.graphViewMode === GraphViewMode.BAR && isSelectedHierarchy;
      const dataset: ChartDataset<"bar" | "line", Point[]> = {
        label:
          this.props.tariffCircuits[this.allTariffCircuits[curSeriesIndex].index]?.name ?? "data",
        data: points,
        type: this.props.graphViewMode === GraphViewMode.LINE || showLinePoints ? "line" : "bar",
        borderColor: isSelectedHierarchy ? cssContent.POWERPROGRESS_GRAYED_OUT : color,
        backgroundColor: isSelectedHierarchy ? cssContent.GRAPH_GRAYED_OUT : color,
        hidden: this.allTariffCircuits[curSeriesIndex].visible === false,
        barThickness: isSelectedHierarchy ? undefined : "flex",
        stack: `${isSelectedHierarchy}`,
        showLine: !showLinePoints,
        pointStyle: showLinePoints ? "point" : undefined,
        pointRadius: showLinePoints ? 2.5 : undefined,
        pointBorderWidth: showLinePoints ? 1 : undefined,
      };
      data.datasets.push(dataset);
    });

    // provide a solution for bar graphs with only one single data element, which has very high thickness
    if (hasRefData) {
      const points: ScatterDataPoint[] = [];
      const overshootPoints: ScatterDataPoint[] = [];
      const refPoints: ScatterDataPoint[] = [];
      // get index from from selected single-line
      const dataIdx = this.allTariffCircuits.findIndex((item) => item.visible === true);

      // add all data points sequentially
      this.data[dataIdx].forEach((curItem, ind) => {
        const curTimestamp = curItem[0] * 1000;
        const curData = curItem[1];
        let curRef: number | undefined;
        if (this.refData.length > ind) {
          curRef = this.refData[ind];
        }

        // data point: in bar chart reduced to target value, as it gets extended with overshoot
        const y =
          curRef === undefined || this.props.graphViewMode === GraphViewMode.LINE
            ? curData
            : Math.min(curData, curRef);
        points.push({
          x: curTimestamp,
          y,
        });

        // target value
        refPoints.push({
          x: curTimestamp,
          y: curRef ?? 0,
        });
        // overshoot; not used in line graph but created anyway
        overshootPoints.push({
          x: curTimestamp,
          y: Math.max(curData - (curRef ?? curData), 0),
        });
      });

      // exchange dataset!
      const color = getColor(this.allTariffCircuits[dataIdx].index);
      data.datasets[dataIdx] = {
        label: this.props.tariffCircuits[this.allTariffCircuits[dataIdx].index]?.name ?? "data",
        data: points,
        hidden: false,
        type: this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar",
        borderColor: color,
        backgroundColor: color,
      };

      // reference points for LINE and BAR graph
      data.datasets[this.allTariffCircuits.length + DATASET_REFERENCE_IDX_OFFSET] = {
        label: "Reference",
        data: refPoints,
        type: "line",
        hidden: false,
        //steppedLine: true,
        pointRadius: 0,
        borderColor: cssContent.GRAPH_COLOR_TARGET,
      };
      // overshoot only for BAR graph
      if (this.props.graphViewMode !== GraphViewMode.LINE) {
        data.datasets[this.allTariffCircuits.length + DATASET_OVERSHOT_IDX_OFFSET] = {
          label: "Overshoot",
          data: overshootPoints,
          type: "bar",
          hidden: false,
          borderColor: cssContent.GRAPH_COLOR_OVERSHOOT,
          backgroundColor: cssContent.GRAPH_COLOR_OVERSHOOT,
        };
      }
    }

    return data;
  }

  private createGraphOptions() {
    const isLegendTop = this.allTariffCircuits.length <= NR_TLS_HIDE_ZERO;
    const options: ChartOptions<"bar" | "line" | "scatter"> = {
      scales: {
        x: {
          type: "time",
          time: {
            //unit: this.useBarChart ? "hour" : "day",
            minUnit: this.minUnits,
            displayFormats: {
              minute: this.props.intl.formatMessage(messages.graphFormatAxisMinute),
              hour: this.props.intl.formatMessage(messages.graphFormatAxisHour),
              day: this.props.intl.formatMessage(messages.graphFormatAxisDay),
            },
          },
          min: this.startTime ? this.startTime * 1000 : undefined,
          // set max date one month earlier at year view?
          max: this.endTime ? this.endTime * 1000 : undefined,
          stacked: this.props.graphViewMode === GraphViewMode.LINE ? false : true,
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: this.getDataUnit(),
          },
          stacked: this.props.graphViewMode === GraphViewMode.LINE ? false : true,
        },
      },
      datasets: {
        line: {
          pointRadius: 2, // default 3
          tension: 0.4, // line smoothness, default 0
          fill: false,
        },
        bar: {
          barThickness: "flex",
        },
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      plugins: {
        legend: {
          display:
            this.props.showLegend &&
            (window.innerWidth > parseInt(cssContent.GRID_BREAKPOINT_MD) || isLegendTop),
          position: isLegendTop ? "top" : "right",
          labels: {
            // only show legend of real tariffs (not added REFERENCE or OVERSHOOT (if any))
            filter: (item: LegendItem, data: ChartData) =>
              isSet(item.datasetIndex) ? item.datasetIndex < this.allTariffCircuits.length : false,
          },
          onClick: (evt, legendItem, legend) => this.legendItemClick(evt, legendItem, legend),
        },
        title: {
          text:
            this.allTariffCircuits.length === 1
              ? this.props.tariffCircuits[this.allTariffCircuits[0].index].name
              : undefined,
        },
        tooltip: {
          enabled: true,
          mode: "point",
          position: "nearest", // show tooltip on average value, not at mouse cursor
          callbacks: {
            title: (tooltipItems: TooltipItem<"bar" | "line">[]) => {
              const curItem = tooltipItems[0];
              const date = new Date(curItem.parsed.x);
              const localeOptions: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "short",
                day: this.props.xZoom !== TimePeriod.YEAR ? "numeric" : undefined,
                hour:
                  this.props.xZoom !== TimePeriod.YEAR && this.props.xZoom !== TimePeriod.MONTH
                    ? "numeric"
                    : undefined,
                minute:
                  this.props.xZoom !== TimePeriod.YEAR && this.props.xZoom !== TimePeriod.MONTH
                    ? "numeric"
                    : undefined,
              };
              return date.toLocaleString(this.props.intl.locale, localeOptions);
            },
            label: (tooltipItem) => this.getTooltipLabel(tooltipItem),
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
            modifierKey: "shift",
            onPanStart: (context) => this.panStartCallback(context.chart, context.point),
            onPanComplete: () => this.panCompleteCallback(),
            onPanRejected: () => this.panCompleteCallback(),
          },
          zoom: {
            mode: "x",
            wheel: {
              enabled: true,
            },
            drag: {
              enabled: true,
              borderColor: hexToRgba(COLOR_SELECT, 0.7),
              backgroundColor: hexToRgba(COLOR_SELECT, 0.5),
            },
            pinch: {
              enabled: true,
            },
            onZoom: (context) => this.zoomCallback(context.chart),
            onZoomStart: (context) => this.zoomCheck(context.chart, context.point),
            onZoomComplete: () => this.zoomCompleteCallback(),
            onZoomRejected: () => this.zoomCompleteCallback(),
          },
          limits: {
            x: {
              min: "original",
              max: "original",
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      animation: {
        duration: 0,
      },
      onClick: (event, elements, chart) => this.onGraphClick(elements),
      onHover: (event, elements, chart) => this.checkMouse(elements),
    };

    return options;
  }

  private getTooltipLabel(tooltipItem: TooltipItem<"bar" | "line">) {
    // hide zero values if the legend is large enough
    // or for reference/overshoot values (they are attached at the end of the selectedTarrifs)
    if (
      (this.allTariffCircuits.length > NR_TLS_HIDE_ZERO && tooltipItem.parsed.y === 0) ||
      tooltipItem.datasetIndex > this.allTariffCircuits.length
    ) {
      return "";
    }
    // special handling for power tooltips
    if (this.props.trendMember === "power") {
      return this.getPowerTooltipLabel(tooltipItem);
    }
    // non-power tooltips
    const propName = tooltipItem.dataset.label ? `${tooltipItem.dataset.label}: ` : "";
    const value = roundToMinDigits(tooltipItem.parsed.y, 2);
    const unit = this.getDataUnit(tooltipItem.datasetIndex);
    return ` ${propName}${value} ${unit}`;
  }

  private getPowerTooltipLabel(tooltipItem: TooltipItem<"bar" | "line">) {
    // for power values, we need to add the overflow bar
    // we need to sum the data and increased data sets to get the total power
    // @@@ Marc -> need rework --> check if current item index > max-available data!
    //    if one higher -> REFERENCE-VALUE; if 2 higher -> OVERSHOOT-VALUE
    const datasets = tooltipItem.chart.data.datasets;
    const showSingleItem = datasets.length > this.allTariffCircuits.length;
    let value = tooltipItem.parsed.y;
    const label = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].label;
    let dataIdx = tooltipItem.datasetIndex;

    // check if we should show special entries for tool-tip if reference-values
    // might be available
    if (showSingleItem) {
      // get index from from selected single-line
      dataIdx = this.allTariffCircuits.findIndex((item) => item.visible === true);
      // get value of the tariff to show (this is the already limited value)
      const dataVal =
        (datasets[dataIdx]?.data?.[tooltipItem.dataIndex] as ScatterDataPoint | undefined)?.y ?? 0;

      // get overshoot-value
      const increasedVal =
        (showSingleItem
          ? (datasets[this.allTariffCircuits.length + DATASET_OVERSHOT_IDX_OFFSET]?.data?.[
              tooltipItem.dataIndex
            ] as ScatterDataPoint | undefined)
          : undefined
        )?.y ?? 0;

      // check if the current tool-tip label is the main-value of the limit or overshoot value
      if (tooltipItem.datasetIndex < this.allTariffCircuits.length) {
        // if the real value should get shown:
        // -> sum limited-value and overshoot
        value = dataVal + increasedVal;
      } else {
        return "";
      }
    }

    // make sure property name is avialble
    const propName = label ? `${label}: ` : "";
    // show prepared name and value -> always use the data-unit of the selected tariff
    return ` ${propName}${roundToMinDigits(value, 2)} ${this.getDataUnit(dataIdx)}`;
  }

  private legendItemClick(evt: ChartEvent, legendItem: LegendItem, legend: any) {
    const index = legendItem.datasetIndex;
    let newSelectedTariff: number[] = [];
    if (index === undefined) {
      return;
    }

    const filtered = this.allTariffCircuits?.filter((tariff) => tariff.visible === false);
    const anyHidden = filtered ? filtered.length > 0 : 0;

    if ((evt.native as PointerEvent).shiftKey) {
      // check if any curve is already hidden...

      // check if the clicked curve is hidden AND if any other curve is also hidden
      if (legendItem.hidden === false && anyHidden) {
        // if yes: show all again
        newSelectedTariff = this.allTariffCircuits?.map((curItem) => curItem.id) ?? [];
      } else {
        // otherwise: hide all except current one.... (leave var empty)
        newSelectedTariff =
          this.allTariffCircuits
            ?.filter((curItem, curIndex) => {
              return curIndex === index;
            })
            .map((curItem) => curItem.id) ?? [];
      }
    } else {
      newSelectedTariff = (
        this.allTariffCircuits?.map((curItem, curIndex) => {
          if (curIndex === index) {
            curItem.visible = Boolean(legendItem.hidden);
          }
          return curItem;
        }) ?? []
      )
        .filter((item) => item?.visible)
        .map((curItem) => curItem.id);
    }

    if (newSelectedTariff.length > 0) {
      this.props.onDropdownTariffSelectionChanged(
        this.props.selectedDashboardIndex,
        newSelectedTariff,
      );
    } else {
      this.setState({ redrawToggle: !this.state.redrawToggle });
    }
  }

  private zoomCallback(chart: ChartJS) {
    const axis = chart.scales.x;
    this.props.onZooming(axis.min, axis.max);
  }

  private resetZoom() {
    this.props.onZooming();
  }

  private zoomCheck(chart: ChartJS, point: Point) {
    this.panLock =
      !this.zoomLock &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;
    return this.panLock;
  }

  private zoomCompleteCallback() {
    this.panLock = false;
  }

  private panStartCallback(chart: ChartJS, point: Point) {
    this.zoomLock =
      !this.panLock &&
      this.props.minX !== undefined &&
      this.props.maxX !== undefined &&
      point.x > chart.chartArea.left &&
      point.x < chart.chartArea.right &&
      point.y > chart.chartArea.top &&
      point.y < chart.chartArea.bottom;

    return this.zoomLock;
  }
  private panCompleteCallback() {
    this.zoomLock = false;
  }

  private onGraphClick(elements: ActiveElement[]) {
    // point selected?
    const activePoint = elements.length ? elements[0] : undefined;
    const timestamp = this.data[activePoint?.datasetIndex ?? 0]?.[activePoint?.index ?? 0]?.[0];
    if (isSet(activePoint) && isSet(timestamp)) {
      // click on data item
      this.props.onDetailClick(timestamp, this.props.xZoom);
    }
  }

  private checkMouse(elements: ActiveElement[]) {
    // point hovered -> show different mouse-pointer
    const activePoint = elements.length ? elements[0] : undefined;
    const timestamp = this.data[activePoint?.datasetIndex ?? 0]?.[activePoint?.index ?? 0]?.[0];

    const element = document.getElementById("energygraph")?.getElementsByTagName("canvas")[0];

    if (isSet(element)) {
      element.style.cursor = isSet(activePoint) && isSet(timestamp) ? "pointer" : "default";
    }
  }
}

const EnergyGraph = connect(mapStateToProps, mapDispatchToProps)(EnergyGraphComponent);

export default injectIntl(EnergyGraph);
